import { NoteBlockModel, NoteDisplayMode } from '@blocksuite/affine-model';
import type { BlockComponent, BlockStdScope } from '@blocksuite/std';
import { GfxBlockElementModel } from '@blocksuite/std/gfx';
import type { BlockModel, Store } from '@blocksuite/store';

import { matchModels } from './checker.js';

export function findAncestorModel(
  model: BlockModel,
  match: (m: BlockModel) => boolean
) {
  let curModel: BlockModel | null = model;
  while (curModel) {
    if (match(curModel)) {
      return curModel;
    }
    curModel = curModel.parent;
  }
  return null;
}

/**
 * Get block component by its model and wait for the doc element to finish updating.
 *
 */
export async function asyncGetBlockComponent(
  std: BlockStdScope,
  id: string
): Promise<BlockComponent | null> {
  const rootBlockId = std.store.root?.id;
  if (!rootBlockId) return null;
  const rootComponent = std.view.getBlock(rootBlockId);
  if (!rootComponent) return null;
  await rootComponent.updateComplete;

  return std.view.getBlock(id);
}

export function findNoteBlockModel(model: BlockModel) {
  return findAncestorModel(model, m =>
    matchModels(m, [NoteBlockModel])
  ) as NoteBlockModel | null;
}

/**
 * Whether a block sits on the canvas itself: a note on the page root, or an
 * image, frame or embed on the surface. A block nested in a note, e.g. an
 * image, is gfx-compatible too but has no canvas position of its own.
 */
export function isPlacedOnCanvas(
  model: BlockModel
): model is GfxBlockElementModel {
  const parent = model.parent?.flavour;
  return (
    model instanceof GfxBlockElementModel &&
    (parent === 'affine:page' || parent === 'affine:surface')
  );
}

/**
 * The block itself or its nearest ancestor placed on the canvas, e.g. the
 * note that holds a paragraph or an image. Its bound stands for blocks the
 * canvas does not render on their own.
 */
export function findCanvasAncestorModel(model: BlockModel) {
  return findAncestorModel(
    model,
    isPlacedOnCanvas
  ) as GfxBlockElementModel | null;
}

export function getLastNoteBlock(doc: Store) {
  let note: NoteBlockModel | null = null;
  if (!doc.root) return null;
  const { children } = doc.root;
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i];
    if (
      matchModels(child, [NoteBlockModel]) &&
      child.props.displayMode !== NoteDisplayMode.EdgelessOnly
    ) {
      note = child as NoteBlockModel;
      break;
    }
  }
  return note;
}

export function getFirstNoteBlock(doc: Store) {
  let note: NoteBlockModel | null = null;
  if (!doc.root) return null;
  const { children } = doc.root;
  for (const child of children) {
    if (
      matchModels(child, [NoteBlockModel]) &&
      child.props.displayMode !== NoteDisplayMode.EdgelessOnly
    ) {
      note = child as NoteBlockModel;
      break;
    }
  }
  return note;
}
